import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { styled, SxProps, Theme, useMediaQuery } from '@mui/material';

import { PauseLargeIcon, PlayIcon } from '../../../assets/icons';
import { ResponsiveImageProps } from '../types';
import { IconButton, Video, VideoContainer } from './video-base';

export type VideoData = {
  posterImage: React.ReactElement<ResponsiveImageProps> | null;
  desktopSources: {
    src: string;
    type: string;
  }[];
  mobileSources?: {
    src: string;
    type: string;
  }[];
};

export type VideoWithControlProps = VideoData & {
  controls?: boolean;
  onPlay?: (isPlaying: boolean) => void;
  sx?: SxProps;
  className?: string;
  onlyPreview?: boolean;
  autoplay?: boolean;
  showAlwaysPauseButton?: boolean;
  loop?: boolean;
  muted?: boolean;
};

enum VideoStatus {
  Stop,
  Play,
  Pause,
}
export const VideoWithControls = ({
  posterImage,
  desktopSources,
  mobileSources,
  controls = true,
  onPlay,
  sx,
  children,
  className = '',
  onlyPreview = false,
  autoplay = false,
  showAlwaysPauseButton = false,
  loop = false,
  muted = false,
}: PropsWithChildren<VideoWithControlProps>) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastAutoplay = useRef<boolean>(autoplay);

  const [status, setStatus] = useState(
    autoplay ? VideoStatus.Play : VideoStatus.Stop,
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const onStartVideo = useCallback(() => {
    setStatus(VideoStatus.Play);
  }, [setStatus]);

  const onPauseVideo = useCallback(() => {
    setStatus(VideoStatus.Pause);
  }, [setStatus]);

  useEffect(() => {
    if (videoRef.current) {
      if (status === VideoStatus.Play) {
        videoRef.current.play().catch((e) => {
          console.error('Video play failed', e);
        });
        if (onPlay) {
          onPlay(true);
        }
      } else {
        videoRef.current.pause();
        if (onPlay) {
          onPlay(false);
        }
      }
    }
  }, [videoRef, status, onPlay]);

  // pause the video if autoplay changes to false and the video is playing
  useEffect(() => {
    if (lastAutoplay.current && !autoplay && status === VideoStatus.Play) {
      setStatus(VideoStatus.Stop);
      lastAutoplay.current = autoplay;
    } else if (
      !lastAutoplay.current &&
      autoplay &&
      status === VideoStatus.Pause
    ) {
      setStatus(VideoStatus.Play);
      lastAutoplay.current = autoplay;
    }
  }, [autoplay, status]);

  const sources = isMobile && mobileSources ? mobileSources : desktopSources;

  const hideVideo = status === VideoStatus.Stop && posterImage;

  return (
    <VideoContainer
      sx={sx}
      className={className}
      showAlwaysPauseButton={showAlwaysPauseButton}
    >
      <Video
        ref={videoRef}
        playsInline
        controls={controls}
        style={{
          visibility: hideVideo ? 'hidden' : undefined,
          maxHeight: hideVideo ? 0 : undefined,
        }}
        onPlay={() => {
          if (!onlyPreview) {
            onStartVideo();
          }
        }}
        disablePictureInPicture
        onPause={() => {
          onPauseVideo();
        }}
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
      >
        {sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </Video>
      {children}
      {status !== VideoStatus.Play ? (
        <>
          {status === VideoStatus.Stop ? (
            <PosterImage>{posterImage}</PosterImage>
          ) : null}
          <PlayButton
            onClick={() => {
              if (!onlyPreview) {
                onStartVideo();
              }
            }}
            variant="text"
            className="play-button"
            startIcon={<PlayIcon />}
          />
        </>
      ) : !controls ? (
        <PlayButton
          onClick={onPauseVideo}
          variant="text"
          className="pause-button"
          startIcon={<PauseLargeIcon />}
        />
      ) : null}
    </VideoContainer>
  );
};

export default VideoWithControls;

const PosterImage = styled('div')({
  position: 'absolute',
  width: '100%',
  height: '100%',

  '.gatsby-image-wrapper': {
    height: '100%',
  },
});

const PlayButton = styled(IconButton)(({ theme }) => ({
  height: '60px',
  width: '60px',
  zIndex: '1',
  display: 'block',
  opacity: 0.7,

  [theme.breakpoints.up('md')]: {
    height: '105px',
    width: '105px',
  },

  svg: {
    color: 'var(--color-dark-coal)',
    transition:
      'transform var(--transition-duration), color var(--transition-duration)',
  },

  '&:hover svg, &:focus-visible svg': {
    transform: 'scale(1.05)',
    color: 'var(--color-light-red)',
  },
}));
